import { ReactNode, useEffect, useRef } from 'react';
import * as S from './ColumnLayout.styled';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { mdiMenu, mdiPlus } from '@mdi/js';
import { MdiIconWrapper } from '@components/MdiIconWrapper';
import Stack from '@mui/material/Stack';
import { Menu } from '../Menu/Menu';
import { useToggle } from '@hooks/useToggle';
import { Fab } from '@mui/material';

const Title = ({ title }: { title: ReactNode | string }) =>
  typeof title === `string` ? (
    <Typography ml={2} variant="h3">
      {title}
    </Typography>
  ) : (
    title
  );

export interface ColumnLayoutProps {
  list: ReactNode[];
  title: ReactNode | string;
  titleComponents?: ReactNode;
  children: ReactNode;
  onNewClick?: () => void;
  scrollTopOnNewListElement?: boolean;
  scrollBottomOnNewListElement?: boolean;
  createButtonTestId?: string;
}

export const ColumnLayout = ({
  list,
  title,
  titleComponents,
  children,
  onNewClick,
  scrollTopOnNewListElement,
  scrollBottomOnNewListElement,
  createButtonTestId = `create-fab`,
}: ColumnLayoutProps) => {
  const [isMenuOpen, toggleIsMenuOpen] = useToggle();
  const listWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listWrapperRef.current && scrollTopOnNewListElement) {
      listWrapperRef.current.scrollTop = 0;
    } else if (listWrapperRef.current && scrollBottomOnNewListElement) {
      listWrapperRef.current.scrollTop = listWrapperRef.current.scrollHeight;
    }
  }, [list?.length, scrollTopOnNewListElement, scrollBottomOnNewListElement]);

  return (
    <>
      <Menu isDrawerOpen={isMenuOpen} toggleIsDrawerOpen={toggleIsMenuOpen} />
      <S.StyledColumnLayout id="page-view-port">
        <S.ListColumn>
          <S.Nav>
            <Stack direction="row" alignItems="center">
              <IconButton onClick={toggleIsMenuOpen} data-testid="menu-button">
                <MdiIconWrapper color="black" path={mdiMenu} />
              </IconButton>
              <Title title={title} />
            </Stack>
            {titleComponents}
          </S.Nav>
          <S.ListWrapper ref={listWrapperRef}>{list}</S.ListWrapper>
          {onNewClick && (
            <S.FabWrapper>
              <Fab
                onClick={onNewClick}
                color="primary"
                sx={{ boxShadow: `none` }}
                size="medium"
                data-testid={createButtonTestId}
              >
                <MdiIconWrapper path={mdiPlus} />
              </Fab>
            </S.FabWrapper>
          )}
        </S.ListColumn>
        {children}
      </S.StyledColumnLayout>
    </>
  );
};
